import {
  booleanAttribute,
  Component, ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
  ViewChild
} from '@angular/core';
import { TendersSearchService } from '../../services/tenders-search.service';
import { hasNonZeroTime } from '../../services/helpers/date-formater';
import { UserTrackerService } from '../../../shared/services/tracking/user-tracker.service';
import { EventTypeName } from '../../../models/user-tracker';
import { Tender } from '../../models/tender';
import { firstValueFrom } from 'rxjs';
import { MarketWatchManager, TendersModuleService } from "../../services/tenders-module.service";
import { isGenerationHourBygone, MarketWatchDayOfWeek } from "../../models/market-watch";
import { findTheoricClosestWatchDate, isSameDay, isSamePeriod, toDayPeriod } from "../../../shared/helpers/date-helper";
import { ApiTenderService } from "../../services/api/api-tender.service";
import { getSanitizedString } from "../../services/helpers/string-helper";
import { StatusItem } from "../../models/tender-status";
import { CdkVirtualScrollViewport } from '@angular/cdk/scrolling';
import { TendersActionService } from '../../services/tenders-action.service';
import { OVERLAY_POSITIONS } from "../../../common-explain/components/ex-dropdown-v2/ex-dropdown-v2.component";
import { TenderTypesEnum } from '../../models/tender-search-body';
import { SmartGridService } from "../../services/smart-grid.service";
import { environment } from "../../../../environments/environment";
import { AutoUnsubscribe } from "../../../common-explain/decorators/auto-unsubscribe";
import { emptyStates } from "./empty-states";

const days = require('../../../shared/helpers/days.json') as MarketWatchDayOfWeek[];

@Component({
  selector: 'app-tenders-list',
  templateUrl: './tenders-list.component.html',
  styleUrls: ['./tenders-list.component.scss']
})
@AutoUnsubscribe
export class TendersListComponent implements OnChanges {
  protected readonly hasNonZeroTime = hasNonZeroTime;
  protected readonly environment = environment;
  protected readonly getSanitizedString = getSanitizedString;
  protected readonly OVERLAY_POSITIONS = OVERLAY_POSITIONS;
  protected readonly TenderTypesEnum = TenderTypesEnum;
  emptyStates = emptyStates;

  @ViewChild("tendersList") tendersList?: CdkVirtualScrollViewport;
  @ViewChild("tendersExclusionsList") tendersExclusionsList?: CdkVirtualScrollViewport;
  @ViewChild('exclusionsButtonRow') exclusionsButtonRow?: ElementRef<HTMLTableRowElement>;

  @Input({required: true}) tendersSearchService!: TendersSearchService;
  @Input() tendersExportService?: TendersActionService;
  @Input() watchManager?: MarketWatchManager;
  @Input() inWatches: boolean = false;
  @Input() isFirstWatch: boolean = false;
  @Input() newParamsNotActiveYet: boolean = false;
  @Input({transform: booleanAttribute}) inBoardView = false;

  @Output() addWatchUsersClicked = new EventEmitter<boolean>();

  hoveredTenderId: string | null = null;
  tenderNoteEditorOpenings: {[key: string]: boolean} = {};
  assigneePopOverId: string | undefined;
  showExcludedTenders = false;

  constructor(
    protected tendersModuleService: TendersModuleService,
    protected apiTendersService: ApiTenderService,
    protected smartGridService: SmartGridService,
    private userTrackerService: UserTrackerService,
  ) {}

  ngOnChanges(changes: SimpleChanges) {
    if (changes['tendersSearchService']) {
      this.scrollToTop();
      // Definition du searchService comme le boardSearchService du module service.
      if (this.inBoardView) this.tendersModuleService.boardSearchService = this.tendersSearchService;
      // Gestion des exclusions
      this.tendersSearchService._filters$.subscribe(() => {
        this.showExcludedTenders = false;
      })
    }
  }

  // Récupération de liste des marchés à afficher.
  get tenderList(){
    if (this.showExcludedTenders) {
      return this.tendersSearchService.tenders?.concat(this.watchManager?.exclusionsService?.tenders ?? [])
    } else {
      return this.tendersSearchService.tenders;
    }
  }

  get currentPage() {
    return (this.tendersSearchService.offset ?? 0) / this.tendersSearchService.limit + 1;
  }

  get loadingMore() {
    if (this.tendersSearchService.inSearch && !this.tendersSearchService.offset) {
      this.scrollToTop();
    }
    return !!(this.tendersSearchService.inSearch && this.tendersSearchService.offset) ||
      !!(this.watchManager?.exclusionsService?.inSearch && this.watchManager?.exclusionsService?.offset);
  }

  loadMore() {
    // Si un service cherche déjà, on stop.
    if (this.tendersSearchService.inSearch || this.watchManager?.exclusionsService?.inSearch) return;
    if (this.tendersSearchService.lastPageReached) {
      // Si tous les marchés ont été chargés, on stop.
      if (this.inWatches && this.watchManager?.exclusionsService?.lastPageReached) return;
      // sinon, s'il reste des marchés exclus, on va les chercher.
      if (this.showExcludedTenders && this.watchManager?.exclusionsService) this.watchManager.exclusionsService.page = this.watchManager?.exclusionsService.loadedPage + 1;
    } else {
      // sinon, s'il reste des marchés, on va les chercher.
      this.tendersSearchService.page = this.tendersSearchService.loadedPage + 1;
    }
  }

  scrollToTop() {
    this.tendersList?.scrollTo({top: 0, behavior: 'smooth'});
  }

  search() {
    this.tendersSearchService.search();
  }

  getFirstPreSearchError(): string {
    if (this.inBoardView) return '';
    if (this.tendersSearchService.tenders) return '';
    if (!this.tendersSearchService.selectedTopics?.length) {
      if (this.tendersSearchService.isObsolete) return 'invalid-topics';
      if (this.isFirstWatch) return 'watch-intro';
      return 'no-text-or-topic';
    }
    if ((!this.tendersSearchService.selectedIndexationPeriod?.from || !this.tendersSearchService.selectedIndexationPeriod?.to) &&
      !(this.tendersSearchService.selectedEstimatedEndPeriod?.from || this.tendersSearchService.selectedEstimatedEndPeriod?.to)) return 'no-period';
    if (!this.tendersSearchService.selectedTerritories?.length) return 'no-perimeter';
    if (this.tendersSearchService.tenderTypes && !this.tendersSearchService.tenderTypes?.length) return 'no-type';
    return ''
  }

  getFirstPostSearchError(): string {
    if (this.inWatches) {
      if (this.tendersSearchService.viewMode === 'preview') { return 'preview-watch-no-results';}
      const now = new Date();
      if (isSamePeriod(this.tendersModuleService.currentManager.service.selectedIndexationPeriod, toDayPeriod(now)) && !isGenerationHourBygone()) return 'today-watch-not-ready';
      if (isSameDay(new Date(this.tendersModuleService.currentManager.marketWatch?.createdAt ?? ''), now) && isGenerationHourBygone()) return 'first-day-watch-not-ready';
      return 'watch-no-results';
    }
    return 'no-results';
  }

  hasScroll() {
    if (this.tendersList)
      return this.tendersList.elementRef.nativeElement.offsetHeight < this.tendersList.elementRef.nativeElement.scrollHeight;
    else return false;
  }

  isToday(date: Date): boolean {
    return date.toDateString() === new Date().toDateString();
  }

  trackClickedTender(tender: Tender) {
    firstValueFrom(this.userTrackerService.track({
      event_type: EventTypeName.TENDER_CLICKED,
      event_timestamp: (new Date()).toISOString(),
      ...this.userTrackerService.buildBasicUserInformations(),
      ...tender,
      filters: this.tendersSearchService.filters,
      rank: (this.tendersSearchService.tenders?.indexOf(tender) ?? 0) + 1,
      score: tender.score ?? 0,
    }));
  }

  getIcon(error: string) {
    return this.emptyStates.find(e => e.error === error)?.icon ?? 'close-circle-fill';
  }

  addMarketWatchUsers() {
    this.addWatchUsersClicked.emit(true);
  }

  //region **status selector methods**
  isEditorOpened(event: {id: string, isOpen: boolean}) {
    this.tenderNoteEditorOpenings[event.id] = event.isOpen;
  }

  noteChange(tender: Tender, note: string) {
    tender.note = note;
    this.tendersModuleService.updateTenderInRegisteredServices(tender.id, {note});
  }

  statusChange(tender: Tender, status?: StatusItem) {
    if (status === undefined) {
      if (this.tendersSearchService.statusCounts?.length)
        this.tendersSearchService.updateStatusCount({from: tender.statusId!, to: null});
    } else {
      const previousStatusId = tender.statusId;
      const newStatusId = status.id;
      tender.statusId = status.id!;
      if (this.tendersSearchService.statusCounts?.length)
        this.tendersSearchService.updateStatusCount({from: previousStatusId ?? null, to: newStatusId!});
    }
    this.tendersModuleService.updateTenderInRegisteredServices(tender.id, {statusId: status?.id ?? undefined});
  }

  //endregion

  findNextWatchDay() {
    const date = findTheoricClosestWatchDate(new Date(), this.tendersModuleService.currentManager.marketWatch?.dayOfWeek, true)
    return days.find(d => d.index === date.getDay())?.value
  }

  identifyTender(index: number, tender: Tender) {
    return tender.id
  }

  toggleOverlay(event: string) {
    this.assigneePopOverId = this.assigneePopOverId === event ? undefined : event;
  }

  closeOverlay() {
    this.assigneePopOverId = undefined;
  }

  isExcludedTender(index: number) {
    return (index > (this.tendersSearchService.tenders ?? []).length - 1);
  }

  onClickExclusions() {
    this.showExcludedTenders = !this.showExcludedTenders;
    if (this.showExcludedTenders) setTimeout(() => {
      this.exclusionsButtonRow?.nativeElement.scrollIntoView({
        behavior: 'instant',
        block: 'start'  // alignement en haut du viewport
      });
    });
  }
}
